/**
 * Mapping partagé des événements du flux d'activité §15.C — réutilisé par
 * le bloc dashboard (EPIC-029 / 29.4) et la page dédiée /activite (29.5).
 *
 * Source de vérité unique : `DOMAIN_LABELS` aligné sur l'enum PHP
 * `App\Enums\ActivityFeedDomain` (14 domaines V1, sans urssaf / das2 /
 * seuils / coffre-fort tant que leurs tables `_event` ne sont pas
 * publiées — cf. décision tranchée EPIC-029).
 */

export const DOMAIN_LABELS = {
    ventes: "Ventes & devis",
    encaissements: "Encaissements",
    relances: "Relances",
    recurrence: "Récurrence",
    achats: "Achats",
    livre_recettes: "Livre des recettes",
    registre_achats: "Registre des achats",
    tva: "TVA",
    cfe: "CFE",
    aides: "Aides",
    pilotage: "Pilotage",
    inpi_compte: "Compte",
    inpi_formality: "Formalité INPI",
    simulation: "Simulation",
};

export function buildEventMapping(event) {
    const domain = typeof event?.domain === "string" ? event.domain : "";
    const eventType = typeof event?.eventType === "string" ? event.eventType : "";
    const metadata =
        event?.metadata && typeof event.metadata === "object" ? event.metadata : {};
    const domainLabel = DOMAIN_LABELS[domain] ?? humanizeDomain(domain);
    const { label, href } = mapEvent(domain, eventType, metadata);
    const details = buildDetails(domain, eventType, metadata);

    return {
        label: label || eventType || "Événement",
        href: href || null,
        domainLabel,
        details,
    };
}

function mapEvent(domain, eventType, metadata) {
    switch (domain) {
        case "ventes":
            return mapVentes(eventType, metadata);
        case "encaissements":
            return mapEncaissements(metadata);
        case "relances":
            return mapRelances(metadata);
        case "recurrence":
            return mapRecurrence(metadata);
        case "achats":
            return { label: achatsLabel(metadata), href: "/factures-recues.html" };
        case "livre_recettes":
            return {
                label: metadata.description ?? "Livre des recettes mis à jour",
                href: "/livre-des-recettes.html",
            };
        case "registre_achats":
            return {
                label: metadata.description ?? "Registre des achats mis à jour",
                href: "/registre-des-achats.html",
            };
        case "tva":
            return { label: tvaLabel(metadata), href: "/tva.html" };
        case "cfe":
            return { label: cfeLabel(metadata), href: "/cfe.html" };
        case "aides":
            return {
                label: metadata.description ?? "Mise à jour aide",
                href: "/aides-assistant.html",
            };
        case "pilotage":
            return { label: pilotageLabel(metadata), href: "/pilotage.html" };
        case "inpi_compte":
            return {
                label: metadata.description ?? "Compte mis à jour",
                href: "/profil-entreprise.html",
            };
        case "inpi_formality":
            return {
                label: metadata.description ?? "Formalité INPI mise à jour",
                href: "/profil-entreprise.html",
            };
        case "simulation":
            return {
                label: metadata.description ?? "Simulation enregistrée",
                href: "/simulateur.html",
            };
        default:
            return { label: eventType, href: null };
    }
}

function mapVentes(eventType, metadata) {
    const invoiceId = metadata.invoiceId ?? null;
    const quoteId = metadata.quoteId ?? null;
    const invoiceNumber = metadata.invoiceNumber ?? "";
    const quoteNumber = metadata.quoteNumber ?? "";
    const clientSuffix = metadata.clientName ? ` — ${metadata.clientName}` : "";

    if (eventType.includes("invoice_overdue")) {
        return {
            label: `Facture ${invoiceNumber} en retard de paiement`.trim(),
            href: invoiceId ? `/facture-detail.html?id=${invoiceId}` : null,
        };
    }
    if (eventType.includes("invoice")) {
        return {
            label: `Facture ${invoiceNumber} émise${clientSuffix}`.trim(),
            href: invoiceId ? `/facture-detail.html?id=${invoiceId}` : null,
        };
    }
    if (eventType.includes("quote_accepted")) {
        return {
            label: `Devis ${quoteNumber} accepté${clientSuffix}`.trim(),
            href: quoteId ? `/devis-detail.html?id=${quoteId}` : null,
        };
    }
    if (eventType.includes("quote")) {
        return {
            label: `Devis ${quoteNumber} envoyé${clientSuffix}`.trim(),
            href: quoteId ? `/devis-detail.html?id=${quoteId}` : null,
        };
    }

    return {
        label: eventType,
        href: invoiceId ? `/facture-detail.html?id=${invoiceId}` : null,
    };
}

function mapEncaissements(metadata) {
    const amount = formatAmount(metadata.amountInclTax ?? metadata.amount);
    const number = metadata.invoiceNumber ?? "";
    const amountSegment = amount ? `de ${amount} ` : "";

    return {
        label: `Paiement ${amountSegment}reçu sur ${number}`.trim(),
        href: metadata.invoiceId ? `/facture-detail.html?id=${metadata.invoiceId}` : null,
    };
}

function mapRelances(metadata) {
    const number = metadata.invoiceNumber ?? "";
    const type = metadata.reminderType ?? "";

    return {
        label: `Relance ${type} envoyée sur ${number}`.replace(/\s+/g, " ").trim(),
        href: metadata.invoiceId ? `/facture-detail.html?id=${metadata.invoiceId}` : null,
    };
}

function mapRecurrence(metadata) {
    const description = metadata.description ?? metadata.invoiceNumber ?? "série";

    return {
        label: `Série récurrente — ${description}`,
        href: metadata.recurringInvoiceId
            ? `/factures-recurrence-detail.html?id=${metadata.recurringInvoiceId}`
            : null,
    };
}

function achatsLabel(metadata) {
    const supplier = metadata.supplierName ?? "fournisseur";

    return `Facture fournisseur reçue — ${supplier}`;
}

function tvaLabel(metadata) {
    const period = metadata.period ?? "";
    const status = metadata.status ? ` — ${metadata.status}` : "";

    return `Déclaration TVA ${period}${status}`.replace(/\s+/g, " ").trim();
}

function cfeLabel(metadata) {
    const year = metadata.year ?? "";
    const description = metadata.description ? ` — ${metadata.description}` : "";

    return `CFE ${year}${description}`.replace(/\s+/g, " ").trim();
}

function pilotageLabel(metadata) {
    if (metadata.description) return metadata.description;
    if (metadata.clientName) {
        return `Concentration client — ${metadata.clientName}`;
    }

    return "Alerte pilotage";
}

function buildDetails(domain, eventType, metadata) {
    switch (domain) {
        case "ventes": {
            const amount = formatAmount(metadata.amountInclTax ?? metadata.amount);

            return amount ? `Montant ${amount}` : "";
        }
        case "encaissements": {
            const mode = metadata.paymentMethod ?? metadata.mode ?? "";

            return mode ? `Mode : ${mode}` : "";
        }
        case "tva": {
            const amount = formatAmount(metadata.vatAmount ?? metadata.amount);

            return amount ? `Montant TVA ${amount}` : "";
        }
        case "achats": {
            const source = metadata.source ?? metadata.canal ?? "";
            const amount = formatAmount(metadata.amountInclTax ?? metadata.amount);
            const sourceLabel = source === "pdp" ? "via PA" : source === "external" ? "upload manuel" : "";
            const parts = [sourceLabel, amount].filter(Boolean);

            return parts.join(" · ");
        }
        case "inpi_formality": {
            const formalityNumber = metadata.formalityNumber ?? metadata.dossierNumber ?? "";
            const status = metadata.status ?? "";
            const parts = [formalityNumber && `N° ${formalityNumber}`, status].filter(Boolean);

            return parts.join(" · ");
        }
        case "inpi_compte": {
            const siret = metadata.siret ?? "";

            return siret ? `SIRET ${siret}` : "";
        }
        default:
            return "";
    }
}

export function formatAmount(value) {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (!Number.isFinite(num)) return "";

    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

export function humanizeDomain(domain) {
    if (!domain) return "Activité";

    return domain
        .split("_")
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join(" ");
}
